import React, { useState, useEffect } from 'react';
import { Clock } from 'lucide-react';

/**
 * Lightweight countdown bis zu einem ISO-Timestamp.
 *
 * Props:
 *  - targetIso: string (z. B. "2026-03-01T18:00:00+00:00")
 *  - className: extra CSS classes for the outer span
 *  - prefix: optional Text vor der Zeit (default: "Endet in")
 *  - compact: kompaktere Variante (kein Prefix, kein Icon)
 *  - onExpired: callback wenn 0 erreicht (optional)
 */
const SaleCountdown = ({ targetIso, className = '', prefix = 'Endet in', compact = false, onExpired }) => {
  const target = targetIso ? new Date(targetIso).getTime() : null;
  const [remaining, setRemaining] = useState(() => (target ? Math.max(0, target - Date.now()) : 0));

  useEffect(() => {
    if (!target) return;
    const tick = () => {
      const left = Math.max(0, target - Date.now());
      setRemaining(left);
      if (left === 0 && onExpired) onExpired();
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [target, onExpired]);

  if (!target || remaining <= 0) return null;

  const totalSec = Math.floor(remaining / 1000);
  const days = Math.floor(totalSec / 86400);
  const hours = Math.floor((totalSec % 86400) / 3600);
  const minutes = Math.floor((totalSec % 3600) / 60);
  const seconds = totalSec % 60;

  // Smart format
  let label;
  if (days >= 1) {
    label = `${days} Tag${days === 1 ? '' : 'e'} ${hours} Std.`;
  } else if (hours >= 1) {
    label = `${hours} Std. ${String(minutes).padStart(2, '0')} Min.`;
  } else {
    label = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')} Min.`;
  }

  if (compact) {
    return (
      <span className={`inline-flex items-center gap-1 text-[10px] font-bold text-[#B0512E] ${className}`}>
        <Clock className="h-3 w-3" />
        {label}
      </span>
    );
  }

  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${className}`}>
      <Clock className="h-3.5 w-3.5" />
      <span>{prefix} <span className="font-bold tabular-nums">{label}</span></span>
    </span>
  );
};

export default SaleCountdown;
